import { stateConfigs, StateConfig } from "./state-config.ts";

export interface DropdownOption {
  value: string;
  label: string; // Optional display label if different from value
  disabled?: boolean;
  default?: boolean;
  order?: number; // Order for sorting programs
}

export interface RangeConfig {
  min: number;
  max: number;
}

export interface Fees {
  underwritingFee: number;
}

export interface ProgramConfig {
  mortgageTypes?: DropdownOption[];
  loanTypes?: DropdownOption[];
  incomeTypes?: DropdownOption[];
  citizenship?: DropdownOption[];
  occupancy: DropdownOption[];
  purpose: DropdownOption[];
  fico: RangeConfig;
  ltv: RangeConfig;
  loanAmount: RangeConfig;
  propertyType: DropdownOption[];
  numberOfUnits: DropdownOption[];
  //state: DropdownOption[];
  dti: DropdownOption[];
  lockPeriod: DropdownOption[];
  amortization: DropdownOption[];
  brokerCompensation?: {
    min: number;
    max: number;
    step: number;
    maxAmount: number;
  };
  fees?: Fees;
  // New properties
  creditEvent: DropdownOption[]; // Added credit event options
  prepaymentPenalty: DropdownOption[]; // Added prepayment penalty options
  lenderFeeBuyOut: boolean; // Added field
  subFinancing: boolean;
  escrowWaiver: boolean;
  firstTimeHomeBuyer: boolean; // Added field
  firstTimeInvestor: boolean; // Added field
  vesting?: DropdownOption[]; // Added vesting options
  mortgageHistory?: DropdownOption[]; // Added mortgage history options
  interestOnly?: boolean; // Added Interest Only checkbox for Non-QM (applies to some NQHEM and NONI)
}

export interface DropdownConfig {
  programs: DropdownOption[];
  programConfigs: Record<string, ProgramConfig>;
  // Common options that don't depend on program selection
  states: DropdownOption[];
}

const commonCreditEvent = [
  { value: "NA", label: "N/A", default: true },
  { value: "84+ Months", label: "84+ Months" },
  { value: "48+ Months", label: "48+ Months" },
  { value: "36 - 48 Months", label: "36 - 48 Months" },
  { value: "24 - 35 Months", label: "24 - 35 Months" },
  { value: "12 - 23 Months", label: "12 - 23 Months" },
];

const commonPrepaymentPenalty = [
  {
    value: "5YR PPP 5% Fixed",
    label: "5YR PPP 5% Fixed",
    default: true,
  },
  {
    value: "4YR PPP 5% Fixed",
    label: "4YR PPP 5% Fixed",
  },
  {
    value: "3YR PPP 5% Fixed",
    label: "3YR PPP 5% Fixed",
  },
  {
    value: "2YR PPP 5% Fixed",
    label: "2YR PPP 5% Fixed",
  },
  {
    value: "1YR PPP 5% Fixed",
    label: "1YR PPP 5% Fixed",
  },
  { value: "No PPP", label: "No PPP" },
  {
    value: "5YR Declining PPP",
    label: "5YR Declining PPP",
  },
  {
    value: "4YR Declining PPP",
    label: "4YR Declining PPP",
  },
  {
    value: "3YR Declining PPP",
    label: "3YR Declining PPP",
  },
  {
    value: "2YR Declining PPP",
    label: "2YR Declining PPP",
  },
  {
    value: "5YR PPP 3% Fixed",
    label: "5YR PPP 3% Fixed",
  },
  {
    value: "4YR PPP 3% Fixed",
    label: "4YR PPP 3% Fixed",
  },
  {
    value: "3YR PPP 3% Fixed",
    label: "3YR PPP 3% Fixed",
  },
  {
    value: "2YR PPP 3% Fixed",
    label: "2YR PPP 3% Fixed",
  },
  {
    value: "1YR PPP 3% Fixed",
    label: "1YR PPP 3% Fixed",
  },
  {
    value: "5YR PPP 6mo Int",
    label: "5YR PPP 6mo Int",
  },
  {
    value: "4YR PPP 6mo Int",
    label: "4YR PPP 6mo Int",
  },
  {
    value: "3YR PPP 6mo Int",
    label: "3YR PPP 6mo Int",
  },
  {
    value: "2YR PPP 6mo Int",
    label: "2YR PPP 6mo Int",
  },
  {
    value: "1YR PPP 6mo Int",
    label: "1YR PPP 6mo Int",
  },
];

// Add this after the commonPrepaymentPenalty constant
const commonVestingOptions = [
  { value: "LLC", label: "LLC" },
  { value: "Partnership", label: "Partnership" },
  { value: "Individual", label: "Individual", default: true },
  { value: "Corporation", label: "Corporation" },
  { value: "Trust", label: "Trust" },
];

// Environment-based program configuration
const getAvailablePrograms = (): string[] => {
  const isProduction = process.env.NODE_ENV === "production";
  const isPreview = process.env.NEXT_PUBLIC_VERCEL_ENV === "preview";

  // Debug logging
  console.log("Environment Debug:", {
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    isProduction,
    isPreview,
    condition: isProduction && !isPreview,
  });

  // In production, only show Non-QM
  if (isProduction && !isPreview) {
    console.log("Returning Non-QM only (production)");
    return ["Non-QM", "Second Lien"];
  }

  // In preview or development, show both Conventional and Non-QM
  console.log("Returning Conventional and Non-QM (preview/development)");
  return ["Conventional", "Non-QM", "Government", "Second Lien"];
};

// Create the configuration based on the CSV data
export const programConfig: DropdownConfig = {
  programs: [
    { value: "Non-QM", label: "Non-QM", order: 1 },
    { value: "Conventional", label: "Conventional", order: 2, default: true },
    { value: "Government", label: "Government", order: 3 },
    { value: "Second Lien", label: "Second Lien", order: 4 },
  ],

  programConfigs: {
    ...(getAvailablePrograms().includes("Conventional")
      ? {
          Conventional: {
            mortgageTypes: [
              { value: "Conventional", label: "Conventional", order: 2 },
            ],
            loanTypes: [
              { value: "Fixed", label: "Fixed", default: true },
              { value: "HomeReady", label: "HomeReady" },
              { value: "HomePossible", label: "Home Possible" },
            ],
            citizenship: [
              {
                value: "US Citizen",
                label: "US Citizen / Permanent Resident",
                default: true,
              },
              {
                value: "Non-Perm Resident",
                label: "Non-Permanent Resident",
              },
            ],
            occupancy: [
              {
                value: "PrimaryResidence",
                label: "Primary Residence",
                default: true,
              },
              { value: "Investment", label: "Investment" },
              { value: "SecondaryVacation", label: "2nd Home" },
            ],
            purpose: [
              { value: "Purchase", label: "Purchase", default: true },
              { value: "Refinance", label: "Rate/Term Refi" },
              { value: "CashoutRefinance", label: "Cashout" },
            ],
            fico: {
              min: 0,
              max: 1000,
            },
            ltv: {
              min: 0,
              max: 200,
            },
            loanAmount: {
              min: 0,
              max: 10000000,
            },
            propertyType: [
              { value: "SingleFamily", label: "SFR", default: true },
              { value: "PlannedUnitDevelopment", label: "PUD" },
              { value: "Condos", label: "Condo" },
              { value: "UnitDwelling_2_4", label: "2-4 Units" },
              { value: "ManufacturedHousing", label: "Manufactured" },
            ],
            numberOfUnits: [
              { value: "1", label: "1-Unit", default: true },
              { value: "2", label: "2-Units" },
              { value: "3", label: "3-Units" },
              { value: "4", label: "4-Units" },
            ],
            dti: [
              { value: "0.20", label: "DTI <= 40%", default: true },
              { value: "0.42", label: "DTI > 40%" },
            ],
            lockPeriod: [
              { value: "15", label: "15 Days", default: true },
              { value: "30", label: "30 Days" },
              { value: "45", label: "45 Days" },
            ],
            amortization: [
              { value: "30", label: "30yr Fixed", default: true },
              { value: "25", label: "25yr Fixed" },
              { value: "20", label: "20yr Fixed" },
              { value: "15", label: "15yr Fixed" },
              { value: "10", label: "10yr Fixed" },
            ],
            brokerCompensation: {
              min: 1.0,
              max: 2.75,
              step: 0.125,
              maxAmount: 20000,
            },
            fees: {
              underwritingFee: 1195,
            },
            // New properties s
            creditEvent: commonCreditEvent,
            prepaymentPenalty: [],
            lenderFeeBuyOut: true,
            subFinancing: false,
            escrowWaiver: true,
            firstTimeHomeBuyer: false,
            firstTimeInvestor: false,
          },
        }
      : {}),
    ...(getAvailablePrograms().includes("Non-QM")
      ? {
          "Non-QM": {
            mortgageTypes: [{ value: "Non-QM", label: "Non-QM", order: 1 }],
            incomeTypes: [
              { value: "DSCR", label: "DSCR" },
              {
                value: "DSCR-AssetUtilization",
                label: "DSCR / Asset Utilization (SuperNONI)",
              },
              {
                value: "Asset Utilization",
                label: "Asset Utilization",
              },
              {
                value: "24MoBusinessBankStatements",
                label: "24 Months Bank Statement",
              },
              {
                value: "12MoBusinessBankStatements",
                label: "12 Months Bank Statement",
              },
              { value: "Full Doc - 24M", label: "2yr Full Doc", default: true },
              { value: "Full Doc - 12M", label: "1yr Full Doc" },

              { value: "24MoCPAPreparedPL", label: "2yr P&L" },
              { value: "12MoCPAPreparedPL", label: "1yr P&L" },
              {
                value: "24MoCPAPreparedPLwBKStmt",
                label: "2yr P&L with 3 month bank statement",
              },
              {
                value: "12MoCPAPreparedPLwBKStmt",
                label: "1yr P&L with 3 month bank statement",
              },
              { value: "VOEOnly", label: "WVOE" },
              { value: "1099 - 12M", label: "1099" },
            ],
            citizenship: [
              {
                value: "US Citizen",
                label: "US Citizen / Permanent Resident",
                default: true,
              },
              {
                value: "Non-Perm Resident",
                label: "Non-Permanent Resident",
              },
              { value: "Foreign National", label: "Foreign National" },
              { value: "ITIN", label: "ITIN" },
            ],
            occupancy: [
              {
                value: "PrimaryResidence",
                label: "Primary Residence",
                default: true,
              },
              { value: "Investment", label: "Investment" },
              { value: "SecondaryVacation", label: "2nd Home" },
            ],
            purpose: [
              { value: "Purchase", label: "Purchase", default: true },
              { value: "Refinance", label: "Rate/Term Refi" },
              { value: "CashoutRefinance", label: "Cashout" },
            ],
            fico: {
              min: 0,
              max: 1000,
            },
            ltv: {
              min: 0,
              max: 200,
            },
            loanAmount: {
              min: 0,
              max: 10000000,
            },
            propertyType: [
              { value: "SingleFamily", label: "SFR", default: true },
              {
                value: "SFR Rural",
                label: "SFR Rural",
              },
              { value: "PlannedUnitDevelopment", label: "PUD" },
              { value: "Condos", label: "Condo" },
              {
                value: "Non-Warrantable Condo",
                label: "Non-Warrantable Condo",
              },
              { value: "UnitDwelling_2_4", label: "2-4 Units" },
              { value: "CondoTel", label: "Condotel" },
              { value: "MultiFamily", label: "5-8 Residential" },
              { value: "2-8 Mixed Use", label: "2-8 Mixed Use" },
            ],
            numberOfUnits: [
              { value: "1", label: "1-Unit", default: true },
              { value: "2", label: "2-Units" },
              { value: "3", label: "3-Units" },
              { value: "4", label: "4-Units" },
              { value: "5", label: "5-Units" },
              { value: "6", label: "6-Units" },
              { value: "7", label: "7-Units" },
              { value: "8", label: "8-Units" },
              { value: "9", label: "9-Units" },
              { value: "10", label: "10-Units" },
            ],
            dti: [
              { value: "0.20", label: "DTI <= 36%", default: true },
              { value: "0.42", label: "DTI > 36% - 43%" },
              { value: "0.46", label: "DTI > 43% - 50%" },
              { value: "0.51", label: "DTI > 50%" },
            ],
            lockPeriod: [
              { value: "15", label: "15 Days", default: true },
              { value: "30", label: "30 Days" },
              { value: "45", label: "45 Days" },
            ],
            amortization: [
              { value: "30-fixed", label: "30yr Fixed", default: true },
              { value: "40-fixed", label: "40yr Fixed" },
              // { value: "15-fixed", label: "15yr Fixed" },
              { value: "30-7-6-ARM", label: "30yr 7/6 ARM" },
              { value: "30-10-6-ARM", label: "30yr 10/6 ARM" },
              { value: "40-7-6-ARM", label: "40yr 7/6 ARM" },
              { value: "40-10-6-ARM", label: "40yr 10/6 ARM" },
            ],
            brokerCompensation: {
              min: 0.0,
              max: 2.75,
              step: 0.125,
              maxAmount: 20000,
            },
            fees: {
              underwritingFee: 1995,
            },
            creditEvent: commonCreditEvent,
            prepaymentPenalty: commonPrepaymentPenalty,
            lenderFeeBuyOut: true,
            subFinancing: false,
            escrowWaiver: true,
            firstTimeHomeBuyer: true,
            firstTimeInvestor: true,
            vesting: commonVestingOptions,
            mortgageHistory: [
              { value: "0x30x12", label: "0x30x12", default: true },
              { value: "0x60x12", label: "0x60x12" },
              { value: "0x90x12", label: "0x90x12" },
            ],
            interestOnly: true, // Show Interest Only checkbox for Non-QM (applies to some NQHEM and NONI)
          },
        }
      : {}),
    ...(getAvailablePrograms().includes("Second Lien")
      ? {
          "Second Lien": {
            mortgageTypes: [
              {
                value: "Non-QM/Second Lien",
                label: "Non-QM Second Lien",
                order: 7,
                default: true,
              },
              // { value: "HELOC", label: "HELOC" },
            ],
            incomeTypes: [
              { value: "Full Doc - 24M", label: "2yr Full Doc" },
              { value: "Full Doc - 12M", label: "1yr Full Doc" },
              {
                value: "Bank Statements - 24M",
                label: "24 Months Bank Statement",
              },
              {
                value: "Bank Statements - 12M",
                label: "12 Months Bank Statement",
              },
              { value: "12MoCPAPreparedPL", label: "1yr P&L" },
              { value: "VOEOnly", label: "WVOE" },
              { value: "1099 - 12M", label: "1099" },
            ],
            citizenship: [
              {
                value: "US Citizen",
                label: "US Citizen / Permanent Resident",
                default: true,
              },
              {
                value: "Non-Perm Resident",
                label: "Non-Permanent Resident",
              },
              { value: "Foreign National", label: "Foreign National" },
            ],
            occupancy: [
              {
                value: "PrimaryResidence",
                label: "Primary Residence",
                default: true,
              },
              { value: "Investment", label: "Investment" },
              { value: "SecondaryVacation", label: "2nd Home" },
            ],
            purpose: [
              { value: "Refinance", label: "Rate/Term Refi" },
              {
                value: "CashoutRefinance",
                label: "Cashout",
                default: true,
              },
            ],
            mortgageHistory: [
              { value: "0x30x12", label: "0x30x12", default: true },
            ],
            fico: {
              min: 0,
              max: 1000,
            },
            ltv: {
              min: 0,
              max: 200,
            },
            loanAmount: {
              min: 0,
              max: 10000000,
            },
            propertyType: [
              { value: "SingleFamily", label: "SFR", default: true },
              {
                value: "SFR Rural",
                label: "SFR Rural",
              },
              { value: "PlannedUnitDevelopment", label: "PUD" },
              { value: "Condos", label: "Condo" },
              { value: "UnitDwelling_2_4", label: "2-4 Units" },
            ],
            numberOfUnits: [
              { value: "1", label: "1-Unit", default: true },
              { value: "2", label: "2-Units" },
              { value: "3", label: "3-Units" },
              { value: "4", label: "4-Units" },
            ],
            dti: [
              { value: "0.42", label: "DTI <= 45%", default: true },
              { value: "0.47", label: "DTI 45.01% - 50%" },
            ],
            lockPeriod: [
              { value: "15", label: "15 Days", default: true },
              { value: "30", label: "30 Days" },
              { value: "45", label: "45 Days" },
              { value: "60", label: "60 Days" },
            ],
            amortization: [
              { value: "10", label: "10 Years", default: true },
              { value: "15", label: "15 Years" },
              { value: "20", label: "20 Years" },
              { value: "25", label: "25 Years" },
              { value: "30", label: "30 Years" },
            ],
            // brokerCompensation: {
            //   min: 1.0,
            //   max: 2.75,
            //   step: 0.125,
            //   maxAmount: 20000,
            // },
            fees: {
              underwritingFee: 999,
            },
            subFinancing: false,
            // New properties
            creditEvent: [
              { value: "NA", label: "N/A", default: true },
              { value: "48+ Months", label: "48+ Months" },
            ],
            prepaymentPenalty: [],
            lenderFeeBuyOut: true,
            //highBalance: true,
            //subFinancing: true,
            escrowWaiver: true,
            firstTimeHomeBuyer: false,
            firstTimeInvestor: false,
          },
        }
      : {}),
    ...(getAvailablePrograms().includes("Government")
      ? {
          Government: {
            mortgageTypes: [
              { value: "FHA", label: "FHA", order: 3, default: true },
              { value: "VA", label: "VA", order: 4 },
              { value: "USDA", label: "USDA", order: 5 },
              //    { value: "theNada (DPA)", label: "theNada (DPA)" },
            ],
            loanTypes: [
              { value: "Fixed", label: "Fixed", default: true },
              { value: "Variable", label: "ARM" },
            ],
            citizenship: [
              {
                value: "US Citizen",
                label: "US Citizen / Permanent Resident",
                default: true,
              },
              {
                value: "Non-Perm Resident",
                label: "Non-Permanent Resident",
              },
            ],
            occupancy: [
              {
                value: "PrimaryResidence",
                label: "Primary Residence",
                default: true,
              },
            ],
            purpose: [
              { value: "Purchase", label: "Purchase", default: true },
              { value: "Refinance", label: "Rate/Term Refi" },
              { value: "CashoutRefinance", label: "Cashout" },
              { value: "StreamLineRefinance", label: "Streamline Refinance" },
            ],
            fico: {
              min: 0,
              max: 1000,
            },
            ltv: {
              min: 0,
              max: 200,
            },
            loanAmount: {
              min: 0,
              max: 10000000,
            },
            propertyType: [
              { value: "SingleFamily", label: "SFR", default: true },
              { value: "PlannedUnitDevelopment", label: "PUD" },
              { value: "Condos", label: "Condo" },
              { value: "UnitDwelling_2_4", label: "2-4 Units" },
              { value: "ManufacturedHousing", label: "Manufactured" },
            ],
            numberOfUnits: [
              { value: "1", label: "1-Unit", default: true },
              { value: "2", label: "2-Units" },
              { value: "3", label: "3-Units" },
              { value: "4", label: "4-Units" },
            ],
            dti: [
              { value: "0.42", label: "DTI < 50%", default: true },
              { value: "0.52", label: "DTI >= 50% & <55%", default: true },
              { value: "0.56", label: "DTI >= 55%" },
            ],
            lockPeriod: [
              { value: "15", label: "15 Days", default: true },
              { value: "30", label: "30 Days" },
              { value: "45", label: "45 Days" },
            ],
            amortization: [
              { value: "30", label: "30yr Fixed", default: true },
              { value: "25", label: "25yr Fixed" },
              { value: "15", label: "15yr Fixed" },
            ],
            brokerCompensation: {
              min: 1.0,
              max: 2.75,
              step: 0.125,
              maxAmount: 20000,
            },
            fees: {
              underwritingFee: 1195,
            },
            // New properties
            creditEvent: commonCreditEvent,
            prepaymentPenalty: [],
            lenderFeeBuyOut: true,
            //  highBalance: true,
            subFinancing: true,
            escrowWaiver: true,
            firstTimeHomeBuyer: false,
            firstTimeInvestor: false,
          },
        }
      : {}),
  },

  // Common options that don't depend on program selection
  states: stateConfigs.map((state: StateConfig) => ({
    label: state.name,
    value: state.abbreviation,
    default: state.abbreviation === "AL", // Setting Alabama as default to match previous behavior
  })),
};

export default programConfig;

// Helper function to get program from mortgage type
function getProgramFromMortgageType(mortgageType: string): string | null {
  for (const [programKey, config] of Object.entries(
    programConfig.programConfigs
  )) {
    if (config.mortgageTypes?.some((mt) => mt.value === mortgageType)) {
      return programKey;
    }
  }
  return null;
}

// Helper functions to get options based on selections
export function getPrograms(): DropdownOption[] {
  return programConfig.programs.sort(
    (a, b) => (a.order || 999) - (b.order || 999)
  );
}

// Returns grouped programs for dropdown: label = program name, options = mortgageTypes
export function getGroupedPrograms() {
  // Get sorted programs first
  const sortedPrograms = getPrograms();

  return Object.entries(programConfig.programConfigs)
    .map(([programKey, config]) => {
      if (config.mortgageTypes && config.mortgageTypes.length > 0) {
        // Sort mortgage types by order
        const sortedMortgageTypes = config.mortgageTypes.sort(
          (a, b) => (a.order || 999) - (b.order || 999)
        );

        // Only show headings for Second Lien, remove headings for Conventional and Non-QM
        if (programKey === "Second Lien") {
          return {
            label: programKey,
            options: sortedMortgageTypes,
            order:
              sortedPrograms.find((p) => p.value === programKey)?.order || 999,
          };
        } else {
          // For Conventional and Non-QM, return the options without a group label
          return {
            label: null,
            options: sortedMortgageTypes,
            order:
              sortedPrograms.find((p) => p.value === programKey)?.order || 999,
          };
        }
      }
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .sort((a, b) => (a.order || 999) - (b.order || 999));
}

export function getIncomeTypes(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];

  // For Government mortgage types (FHA, VA, USDA), we don't want to show the income type field
  if (program === "Government") {
    return [];
  }
  return programConfig.programConfigs[program]?.incomeTypes || [];
}

export function getAmortizationTypes(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.amortization || [];
}

export function getCitizenshipOptions(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.citizenship || [];
}

export function getOccupancyOptions(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.occupancy || [];
}

export function getPurposeOptions(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];

  const purposeOptions = programConfig.programConfigs[program]?.purpose || [];

  // Special handling for VA mortgage type - change "Streamline Refinance" label to "VA IRRRL"
  if (mortgageType === "VA") {
    return purposeOptions.map((option) => {
      if (option.value === "StreamLineRefinance") {
        return {
          ...option,
          label: "VA IRRRL",
        };
      }
      return option;
    });
  }

  return purposeOptions;
}

export function getPropertyTypeOptions(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.propertyType || [];
}

export function getNumberOfUnitsOptions(
  mortgageType: string
): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.numberOfUnits || [];
}

export function getDtiOptions(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.dti || [];
}

export function getLockPeriodOptions(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.lockPeriod || [];
}

export function getBrokerCompensationRange(input: string): {
  min: number;
  max: number;
  step: number;
  maxAmount: number;
} {
  // First, check if input is a program key directly
  let program = programConfig.programConfigs[input] ? input : null;
  // If not, try to resolve as mortgageType
  if (!program) {
    program = getProgramFromMortgageType(input);
  }
  if (!program) {
    return {
      min: 1.0,
      max: 2.75,
      step: 0.125,
      maxAmount: 20000,
    };
  }
  return (
    programConfig.programConfigs[program]?.brokerCompensation || {
      min: 1.0,
      max: 2.75,
      step: 0.125,
      maxAmount: 20000,
    }
  );
}

export function getLoanAmountRange(mortgageType: string): {
  min: number;
  max: number;
} {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) {
    return {
      min: 50000,
      max: 2300000,
    };
  }
  return (
    programConfig.programConfigs[program]?.loanAmount || {
      min: 50000,
      max: 2300000,
    }
  );
}

export function getCreditEventOptions(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.creditEvent || [];
}

export function isNoni58Scenario(params: {
  incomeType: string;
  occupancy: string;
  propertyType: string;
  numberOfUnits?: string;
}): boolean {
  const { incomeType, occupancy, propertyType, numberOfUnits } = params;

  if (incomeType !== "DSCR" || occupancy !== "Investment") {
    return false;
  }

  const units = Number(numberOfUnits);
  if (isNaN(units)) {
    return false;
  }

  if (propertyType === "MultiFamily") {
    return units >= 5 && units <= 8;
  }

  if (propertyType === "2-8 Mixed Use") {
    return units >= 2 && units <= 8;
  }

  return false;
}

export function getPrepaymentPenaltyOptions(
  mortgageType: string,
  occupancy: string,
  incomeType: string,
  propertyType?: string,
  numberOfUnits?: string
): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];

  let options = programConfig.programConfigs[program]?.prepaymentPenalty || [];

  if (options.length === 0) {
    return options;
  }

  if (occupancy !== "Investment") {
    return [{ value: "null", label: "No PPP", default: true }];
  }

  // NONI58 only allows 5% fixed PPP per updated guidelines
  if (
    isNoni58Scenario({
      incomeType,
      occupancy,
      propertyType: propertyType || "",
      numberOfUnits,
    })
  ) {
    options = options.filter(
      (opt: DropdownOption) =>
        opt.value.includes("5% Fixed") || opt.value === "No PPP"
    );
    return options;
  }

  // Only show 3% and 6-month interest PPP options for DSCR income types
  if (!incomeType?.includes("DSCR")) {
    options = options.filter(
      (opt: DropdownOption) =>
        !opt.value.includes("3% Fixed") && !opt.value.includes("6mo Int")
    );
  }
  return options;
}

export function getLoanTypes(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.loanTypes || [];
}

export function getVestingOptions(mortgageType: string): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.vesting || [];
}

export function getMortgageHistoryOptions(
  mortgageType: string
): DropdownOption[] {
  const program = getProgramFromMortgageType(mortgageType);
  if (!program) return [];
  return programConfig.programConfigs[program]?.mortgageHistory || [];
}

// Utility function to get the label for a given occupancy value
export function getOccupancyLabel(occupancyValue: string): string {
  // Search across all programs
  for (const programKey in programConfig.programConfigs) {
    const occupancyOptions =
      programConfig.programConfigs[programKey]?.occupancy || [];
    const option = occupancyOptions.find((opt) => opt.value === occupancyValue);
    if (option) {
      return option.label;
    }
  }
  return occupancyValue; // Fallback to value if label not found
}

// Utility function to get the label for a given purpose value
export function getPurposeLabel(purposeValue: string): string {
  // Search across all programs
  for (const programKey in programConfig.programConfigs) {
    const purposeOptions =
      programConfig.programConfigs[programKey]?.purpose || [];
    const option = purposeOptions.find((opt) => opt.value === purposeValue);
    if (option) {
      return option.label;
    }
  }
  return purposeValue; // Fallback to value if label not found
}

// Utility function to get the label for a given property type value
export function getPropertyTypeLabel(propertyTypeValue: string): string {
  console.log("propertyTypeValue", propertyTypeValue);

  // Search across all programs
  for (const programKey in programConfig.programConfigs) {
    const propertyTypeOptions =
      programConfig.programConfigs[programKey]?.propertyType || [];
    const option = propertyTypeOptions.find(
      (opt) => opt.value === propertyTypeValue
    );
    if (option) {
      console.log("propertyTypeOptions", propertyTypeOptions);
      console.log("propertyTypeValue", propertyTypeValue);
      console.log("option", option);
      return option.label;
    }
  }
  return propertyTypeValue; // Fallback to value if label not found
}

// Utility function to get the label for a given income type value
export function getIncomeTypeLabel(incomeTypeValue: string): string {
  // Search across all programs
  for (const programKey in programConfig.programConfigs) {
    const incomeTypeOptions =
      programConfig.programConfigs[programKey]?.incomeTypes || [];
    const option = incomeTypeOptions.find(
      (opt) => opt.value === incomeTypeValue
    );
    if (option) {
      return option.label;
    }
  }
  return incomeTypeValue; // Fallback to value if label not found
}
